import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  FunctionField,
  EditButton,
  useTranslate,
  TopToolbar,
  RefreshButton,
  BooleanField,
  useListContext,
  RecordContextProvider,
} from 'react-admin';
import {
  Chip,
  Box,
  Typography,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Warning,
  Settings,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const DesktopListActions = () => (
  <TopToolbar>
    <RefreshButton />
  </TopToolbar>
);

const MobileListActions = () => (
  <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0 }}>
    <RefreshButton />
  </Box>
);

const ActiveChip = ({ isActive }: { isActive: boolean }) => {
  const translate = useTranslate();

  return (
    <Chip
      label={isActive ? translate('common.active') : translate('common.inactive')}
      color={isActive ? 'success' : 'default'}
      size="small"
      icon={isActive ? <CheckCircle fontSize="small" /> : <Cancel fontSize="small" />}
    />
  );
};

const ConfiguredChip = ({ isConfigured }: { isConfigured: boolean }) => {
  const translate = useTranslate();

  return (
    <Tooltip title={isConfigured
      ? translate('resources.payment-gateways.hints.configured')
      : translate('resources.payment-gateways.hints.not_configured')}>
      <Chip
        label={isConfigured
          ? translate('resources.payment-gateways.status.configured')
          : translate('resources.payment-gateways.status.not_configured')}
        color={isConfigured ? 'success' : 'warning'}
        size="small"
        icon={isConfigured ? <CheckCircle fontSize="small" /> : <Warning fontSize="small" />}
      />
    </Tooltip>
  );
};

const MobileList = () => {
  const { data, isLoading } = useListContext();
  const navigate = useNavigate();
  const translate = useTranslate();
  if (isLoading || !data) return null;
  return (
    <Box>
      {data.map((record: any) => (
        <RecordContextProvider key={record.id} value={record}>
          <Box
            onClick={() => navigate(`/payment-gateways/${record.id}`)}
            sx={{ py: 1.5, px: 2, borderBottom: '1px solid #F0F0F0', cursor: 'pointer' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                <Settings fontSize="small" color="action" />
                <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', textTransform: 'uppercase' }}>
                  {record.code}
                </Typography>
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <ActiveChip isActive={record.is_active} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {record.name}
              </Typography>
              <ConfiguredChip isConfigured={record.is_configured} />
            </Box>
          </Box>
        </RecordContextProvider>
      ))}
    </Box>
  );
};

export const PaymentGatewaysList = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <List
      actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
      perPage={25}
      sort={{ field: 'code', order: 'ASC' }}
      empty={isMobile ? false : undefined}
      sx={{
        '& .RaList-content': {
          backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
        },
        ...(isMobile && {
          '& .RaList-main': { margin: 0 },
          '& .RaList-content': { backgroundColor: 'transparent !important', boxShadow: 'none !important', mt: 2 },
          '& .RaList-actions': { backgroundColor: 'transparent !important', p: '0 !important', minHeight: 'auto !important' },
          '& .RaListToolbar-root': { backgroundColor: 'transparent !important', flexDirection: 'row !important', flexWrap: 'wrap !important', alignItems: 'center !important', gap: '8px !important' },
          '& .RaListToolbar-root > span': { display: 'none' },
          '& .RaFilterButton-root, & .add-filter': { display: 'none !important' },
          '& .RaListToolbar-root > form, & .RaFilterForm-root': { flex: '1 1 100% !important', width: '100% !important' },
          '& .filter-field, & .RaFilterForm-filterFormInput': { width: '100% !important' },
          '& .filter-field .MuiFormControl-root': { width: '100% !important' },
          '& .filter-field .MuiInputBase-root': { width: '100% !important' },
          '& .MuiToolbar-root': { backgroundColor: 'transparent !important', minHeight: 'auto !important' },
        }),
      }}
    >
      {isMobile ? (
        <MobileList />
      ) : (
        <Datagrid
          bulkActionButtons={false}
          rowClick="edit"
          sx={{
            '& .RaDatagrid-headerCell': {
              backgroundColor: '#F5F5F5',
              fontWeight: 'bold',
            },
            '& .RaDatagrid-row': {
              '&:hover': {
                backgroundColor: '#F0F7FF',
              },
            },
            '& .column-code': { width: '120px' },
            '& .column-name': { minWidth: '200px', flex: 1 },
            '& .column-is_configured': { width: '150px' },
            '& .column-tokenization': { width: '130px' },
            '& .column-is_active': { width: '100px' },
            '& .column-actions': { width: '80px' },
          }}
        >
          <FunctionField
            label={translate('resources.payment-gateways.fields.code')}
            render={(record: any) => (
              <Box display="flex" alignItems="center" gap={1}>
                <Settings fontSize="small" color="action" />
                <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'uppercase' }}>
                  {record.code}
                </Typography>
              </Box>
            )}
            source="code"
          />

          <TextField
            source="name"
            label={translate('resources.payment-gateways.fields.name')}
          />

          <FunctionField
            label={translate('resources.payment-gateways.fields.is_configured')}
            render={(record: any) => (
              <ConfiguredChip isConfigured={record.is_configured} />
            )}
            source="is_configured"
          />

          <FunctionField
            label={translate('resources.payment-gateways.fields.supports_tokenization')}
            render={(record: any) => (
              record.supports_tokenization ? (
                <Chip
                  label={translate('common.yes')}
                  size="small"
                  color="info"
                />
              ) : (
                <Typography variant="body2" color="text.secondary">-</Typography>
              )
            )}
            source="tokenization"
          />

          <FunctionField
            label={translate('resources.payment-gateways.fields.is_active')}
            render={(record: any) => (
              <ActiveChip isActive={record.is_active} />
            )}
            source="is_active"
          />

          <FunctionField
            label=""
            render={(record: any) => (
              <Box display="flex" gap={0}>
                <Tooltip title={translate('ra.action.edit')}>
                  <span>
                    <EditButton record={record} size="small" label="" />
                  </span>
                </Tooltip>
              </Box>
            )}
            source="actions"
            textAlign="right"
          />
        </Datagrid>
      )}
    </List>
  );
};
